import { getCommon } from 'app-lib/assetUtils'
import hashUtils from 'app-lib/hashUtils'
import { autoFormat } from 'app-lib/numberUtils'
import { getBlockExplorerAddressLink, links, stakingConfig } from 'bzx-common/src/config/appConfig'
import { observer } from 'mobx-react'
import React from 'react'
import ExternalLink from 'shared-components/ExternalLink'
import { Button, Icon, Input, Loader } from 'ui-framework'
import { ReactComponent as Govern } from './dao-govern.svg'
import DaoVM from './DaoVM'
import OnChainProposals from './OnChainProposals'
import Proposals from './Proposals'

const loader = <Loader className="staking__balance-loader" quantityDots={3} sizeDots="small" />

export function Dao({ vm }: { vm: DaoVM }) {
  const { dao } = vm.stakingStore
  const votingPower = dao.votingPowerTotal.isZero() ? dao.votingPowerStaked : dao.votingPowerTotal

  return (
    <>
      <div className="grid--staking margin-bottom-1">
        <div className="panel--simple md-padded-2 pos-rel flex-col height-100p">
          {dao.votingPowerPending && loader}
          <h3 className="title-section panel__header flex-row-center">
            Governance
            {!dao.votingPowerPending && (
              <ExternalLink
                className="link--discreet margin-left-1ch flex-row-end"
                href={links.docs.daoGovernance}>
                <Icon size={24} name="info" />
              </ExternalLink>
            )}
          </h3>
          <div className="txt-center margin-bottom-2">
            <div className="txt-center margin-bottom-2">
              <Govern width="50" className="margin-bottom-1" />
              <h4 className="h4--muted">Your voting power</h4>
            </div>
            <b className="txt-size-12">{autoFormat(votingPower)}</b>
          </div>
          {dao.votingPowerFromDelegators.gt(0) && (
            <p className="txt-explanation txt-center flex-col-end">
              This includes <b>{autoFormat(dao.votingPowerFromDelegators)}</b> voting power coming
              from your delegators.
            </p>
          )}
          {dao.hasDelegate && (
            <i className="txt-explanation txt-center flex-col-end">You are delegating</i>
          )}
        </div>

        <div className="panel--simple md-padded-2 pos-rel">
          {dao.getDelegatePending && loader}
          <h3 className="title-section panel__header flex-row-center">
            Delegate
            {!dao.getDelegatePending && (
              <ExternalLink
                className="link--discreet margin-left-1ch flex-row-end"
                href={links.docs.voteDelegation}>
                <Icon size={24} name="info" />
              </ExternalLink>
            )}
          </h3>
          <div className="txt-explanation">
            <p>
              Someone you trust can vote on your behalf. You have the possibility to give them your
              voting power.
            </p>

            {dao.hasDelegate && (
              <div>
                <p>
                  Your are currently delegating to:{' '}
                  <ExternalLink
                    className="link--discreet"
                    href={getBlockExplorerAddressLink(1, dao.delegateAddress)}>
                    {hashUtils.shortHash(dao.delegateAddress)}
                  </ExternalLink>
                </p>
                <Button className="flex-row-end" variant="red" onClick={dao.stopDelegating}>
                  Stop Delegating
                </Button>
              </div>
            )}

            {!dao.hasDelegate && (
              <div>
                <form onSubmit={vm.setDelegate} className="margin-bottom-1">
                  <label
                    className="txt-subtitle inline-block margin-bottom-05"
                    htmlFor="inputDelegate">
                    Delegate address
                  </label>
                  <div className="input-with-sticked-btn">
                    <Input
                      id="inputDelegate"
                      placeholder="0x"
                      onChangeEmit="name-value"
                      name="inputDelegate"
                      value={vm.inputDelegate}
                      onChange={vm.set}
                    />
                    <Button
                      variant="green"
                      type="submit"
                      disabled={!vm.canDelegate}
                      isLoading={dao.setDelegatePending}>
                      Delegate
                    </Button>
                  </div>
                </form>
                <p>
                  You can remove this power at any time and this does not give them control of your
                  staked tokens.
                </p>
              </div>
            )}
            <h4 className="h4--muted margin-top-2">Vote Delegator Contract</h4>

            <ExternalLink className="link--discreet" href={stakingConfig.voteDelegatorUrl}>
              {hashUtils.shortHash(stakingConfig.voteDelegatorContract, 8, 8)}
            </ExternalLink>
          </div>
        </div>
      </div>

      <div className="btn--sticked-horizontal margin-v-2">
        <Button
          variant="tab-blue"
          active={vm.daoSection === 'snapshotProposals'}
          name="daoSection"
          value="snapshotProposals"
          onClick={vm.setSection}
          onClickEmit="value">
          Snapshot Votes
        </Button>
        <Button
          variant="tab-blue"
          active={vm.daoSection === 'onchainProposals'}
          name="daoSection"
          value="onchainProposals"
          onClick={vm.setSection}
          onClickEmit="value">
          Onchain Votes
        </Button>
      </div>

      {vm.daoSection === 'snapshotProposals' && <Proposals vm={vm} />}
      {vm.daoSection === 'onchainProposals' && <OnChainProposals vm={vm} />}

      {dao.globalStaked.length > 0 && (
        <div className="panel--simple md-padded-2 ">
          <h3 className="title-section">Total staked by all users</h3>
          <div className="grid-fluid-colmin-10em txt-explanation">
            {dao.globalStaked.map((staked) => {
              const { displayName, spriteLogo } = getCommon(staked.token)
              return (
                <div key={staked.token}>
                  <div>
                    <Icon size={24} name={spriteLogo} /> <b>{displayName}</b>
                  </div>{' '}
                  <div className="txt-default">{autoFormat(staked.amount)}</div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

export default observer(Dao)
